import { MapPin, Globe2, HeartHandshake, MessagesSquare, Puzzle, Sprout } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionHeading } from "../components/site/SectionHeading";
import { Reveal } from "../components/site/Reveal";
import { CtaBand } from "../components/site/CtaBand";
import { useSeo } from "../lib/useSeo";

const VALUES = [
  { icon: MapPin, t: "Sitz in Lübeck, Vermittlung in ganz Deutschland", d: "Von Lübeck aus arbeiten wir mit Unternehmen in allen Regionen Deutschlands zusammen – vom Handwerksbetrieb bis zur Klinik." },
  { icon: Globe2, t: "Internationale Personalgewinnung", d: "Wir gewinnen Arbeitskräfte aus dem Ausland und verbinden sie mit Arbeitgebern, die ihre Fähigkeiten brauchen." },
  { icon: HeartHandshake, t: "Persönliche Betreuung", d: "Unternehmen und Bewerber haben bei uns feste Ansprechpartner, die den Stand kennen und erreichbar sind." },
  { icon: MessagesSquare, t: "Klare Kommunikation", d: "Wir erklären Abläufe verständlich, sagen offen, was möglich ist – und was nicht – und halten Vereinbarungen schriftlich fest." },
  { icon: Puzzle, t: "Passung vor Geschwindigkeit", d: "Uns ist wichtig, dass Anforderungen, Qualifikation und Erwartungen auf beiden Seiten zusammenpassen." },
  { icon: Sprout, t: "Langfristige Arbeitsverhältnisse", d: "Unser Ziel sind Arbeitsverhältnisse, die für Arbeitgeber und Arbeitskräfte dauerhaft funktionieren." },
];

export default function About() {
  useSeo("Über PACTORA", "PACTORA Personalpartner mit Sitz in Lübeck: internationale Personalgewinnung, persönliche Betreuung und nachvollziehbare Abläufe für Unternehmen in ganz Deutschland.", "/ueber-uns");
  return (
    <>
      <PageHero testid="about-hero" eyebrow="Über PACTORA" lines={["Wir verbinden Menschen", "und Arbeit – über Grenzen hinweg."]}
        intro="PACTORA Personalpartner vermittelt Arbeitskräfte aus dem Ausland in direkte Arbeitsverhältnisse bei Unternehmen in ganz Deutschland. Wir verstehen uns als Partner beider Seiten: für Arbeitgeber, die verlässlich Personal suchen, und für Menschen, die sich in Deutschland eine berufliche Zukunft aufbauen möchten."
        image="/img/luebeck.webp" imageAlt="Holstentor in Lübeck" />
      <section className="bg-white pb-24 md:pb-32" data-testid="about-story">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><p className="eyebrow">Unser Anspruch</p></Reveal>
          <Reveal delay={0.08} className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-slate-700">
            <p>Internationale Personalgewinnung ist mehr als das Weiterreichen von Lebensläufen. Sie verlangt ein genaues Verständnis davon, was ein Unternehmen braucht, was eine Bewerberin oder ein Bewerber mitbringt – und welche Schritte bis zur Arbeitsaufnahme nötig sind.</p>
            <p>Deshalb arbeiten wir mit klaren Abläufen: Wir klären den Bedarf gründlich, wählen sorgfältig aus, stellen nur passende Profile vor und begleiten die organisatorische Abstimmung rund um Bewerbung, Einstellung, Unterlagen, Visum und Arbeitsbeginn. Über Visa, Aufenthaltstitel und Anerkennungen entscheiden die Behörden – wir sorgen dafür, dass alle Beteiligten wissen, was zu tun ist.</p>
          </Reveal>
        </div>
      </section>
      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="about-values">
        <div className="container-x">
          <SectionHeading eyebrow="Wofür wir stehen" title="Sechs Grundsätze unserer Arbeit" />
          <div className="mt-14 grid gap-px overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={(i % 3) * 0.05} className="bg-white">
                <article className="h-full p-8 md:p-10" data-testid={`about-value-${i}`}>
                  <v.icon size={26} className="text-[color:var(--teal-ink)]" />
                  <h3 className="mt-6 text-lg font-bold text-[color:var(--navy)]">{v.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
